import React, { useState, useEffect } from 'react';
import { useModel } from '../context/ModelContext';
import { TariffConfiguration, TariffRule, Scenario, KappaConfig, EMPTY_TARIFF_CONFIG, hasTariffShock, hasKappaShock } from '../types/model';
import MultiSelect from './shared/MultiSelect';
import { DATASETS, MODEL_TARGETS } from '../utils/modelTargets';
import TariffOverrideTable from './shared/TariffOverrideTable';
import KappaShockEditor from './shared/KappaShockEditor';

const ALL = 'All';
const TRADABLE_SECTORS = 'Tradable Sectors';
const NONTRADABLE_SECTORS = 'Non-Tradable Sectors';
// "Electricity, Gas and Water" is the first non-tradable sector (index 10 in Eora26)
const NONTRADABLE_BOUNDARY = 'Electricity, Gas and Water';

/** Expand a sector wildcard ('All', 'Tradable Sectors', 'Non-Tradable Sectors') into sector names. */
function expandSectorWildcard(wildcard: string, sectors: string[]): string[] {
  if (wildcard === ALL) return sectors;
  if (wildcard === TRADABLE_SECTORS) {
    const boundary = sectors.indexOf(NONTRADABLE_BOUNDARY);
    return boundary >= 0 ? sectors.slice(0, boundary) : sectors;
  }
  if (wildcard === NONTRADABLE_SECTORS) {
    const boundary = sectors.indexOf(NONTRADABLE_BOUNDARY);
    return boundary >= 0 ? sectors.slice(boundary) : [];
  }
  return [wildcard];
}

type TariffMode = 'uniform' | 'custom';

interface ScenarioEditorProps {
  isOpen: boolean;
  onClose: () => void;
  editingScenario?: Scenario | null;
}

const detectTariffMode = (
  config: TariffConfiguration
): { mode: TariffMode; rules: TariffRule[] } => {
  if (config.tariffMode === 'uniform') return { mode: 'uniform', rules: [] };
  if (config.customRules) return { mode: 'custom', rules: config.customRules };
  const rules = Object.entries(config.tariff_data).map(([key, value]) => {
    const [importer, exporter, sector] = key.split('|');
    return { importer, exporter, sector, ...value };
  });
  // API/chat configs have no editor metadata. Recover uniform controls only when
  // the populated cells exactly cover the selected foreign-trade rectangle.
  const first = rules[0];
  const expected = config.importers.reduce((count, importer) => count + config.exporters.filter(exporter => importer !== exporter).length, 0) * config.sectors.length;
  if (first && rules.length === expected && rules.every(rule => rule.rate === first.rate && rule.mode === first.mode && config.importers.includes(rule.importer) && config.exporters.includes(rule.exporter) && config.sectors.includes(rule.sector))) {
    return { mode: 'uniform', rules: [] };
  }
  return { mode: rules.length ? 'custom' : 'uniform', rules };
};

const EMPTY_KAPPA_CONFIG: KappaConfig = { kappa_S: {}, kappa_D: {} };

const ScenarioEditor: React.FC<ScenarioEditorProps> = ({ isOpen, onClose, editingScenario }) => {
  const {
    baselines, ensureBaseline, framework, dataset, frameworkInfo, addScenario,
    getNextScenarioName, solvingCount,
  } = useModel();


  const [name, setName] = useState('');
  const [selectedDataset, setSelectedDataset] = useState(editingScenario?.dataset || dataset || DATASETS[0].id);
  const [selectedFrameworks, setSelectedFrameworks] = useState([editingScenario?.framework || framework || MODEL_TARGETS[0].framework]);
  const [modelError, setModelError] = useState<string | null>(null);
  const [modelRetry, setModelRetry] = useState(0);
  const availableTargets = MODEL_TARGETS.filter(t => t.dataset === selectedDataset);
  const targets = editingScenario ? [editingScenario] : availableTargets.filter(t => selectedFrameworks.includes(t.framework));
  const primaryTarget = targets[0];
  const baselineModel = primaryTarget ? baselines[`${primaryTarget.framework}/${primaryTarget.dataset}`] : undefined;
  const isLoading = targets.some(t => !baselines[`${t.framework}/${t.dataset}`]);
  const currentFrameworkSupportsKappa = targets.length === 1 && !!frameworkInfo[targets[0].framework]?.supports_kappa;

  useEffect(() => {
    let active = true;
    const requested = editingScenario ? [editingScenario] : MODEL_TARGETS.filter(t => t.dataset === selectedDataset && selectedFrameworks.includes(t.framework));
    setModelError(null);
    void Promise.all(requested.map(t => ensureBaseline(t.framework, t.dataset))).catch(() => {
      if (active) setModelError('Could not load the selected models.');
    });
    return () => { active = false; };
  }, [selectedDataset, selectedFrameworks, editingScenario, ensureBaseline, modelRetry]);

  // Two independent, composable shock sections.
  const [tariffEnabled, setTariffEnabled] = useState(true);
  const [kappaEnabled, setKappaEnabled] = useState(false);
  const [kappaConfig, setKappaConfig] = useState<KappaConfig>(EMPTY_KAPPA_CONFIG);


  // Uniform mode state
  const [tariffRate, setTariffRate] = useState(20);
  const [mode, setMode] = useState<'Increment' | 'Set Level'>('Increment');
  const [selectedImporters, setSelectedImporters] = useState<string[] | null>([]);
  const [selectedExporters, setSelectedExporters] = useState<string[] | null>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[] | null>([]);

  // Custom mode state
  const [customRules, setCustomRules] = useState<TariffRule[]>([]);

  // Mode toggle
  const [tariffMode, setTariffMode] = useState<TariffMode>('uniform');

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (editingScenario) {
      setName(editingScenario.status === 'solved' ? `${editingScenario.name} · revision` : editingScenario.name);

      const kappaShockPresent = hasKappaShock(editingScenario.kappaConfig);
      setKappaEnabled(kappaShockPresent);
      setKappaConfig(editingScenario.kappaConfig ?? EMPTY_KAPPA_CONFIG);

      const tariffShockPresent = hasTariffShock(editingScenario.tariffConfig);
      setTariffEnabled(tariffShockPresent);

      if (!tariffShockPresent) {
        setTariffMode('uniform');
        setSelectedImporters([]);
        setSelectedExporters([]);
        setSelectedSectors([]);
        setCustomRules([]);
        setTariffRate(20);
        setMode('Increment');
        return;
      }

      const { mode: detectedMode, rules } = detectTariffMode(editingScenario.tariffConfig);
      setTariffMode(detectedMode);

      if (detectedMode === 'uniform') {
        setSelectedImporters(editingScenario.tariffConfig.importers);
        setSelectedExporters(editingScenario.tariffConfig.exporters);
        setSelectedSectors(editingScenario.tariffConfig.sectors);
        const firstTariff = Object.values(editingScenario.tariffConfig.tariff_data)[0];
        if (firstTariff) {
          setTariffRate(firstTariff.rate);
          setMode(firstTariff.mode);
        }
        setCustomRules([]);
      } else {
        setCustomRules(rules);
        // Clear uniform state
        setSelectedImporters([]);
        setSelectedExporters([]);
        setSelectedSectors([]);
        setTariffRate(20);
        setMode('Increment');
      }
    } else {
      // Reset for new scenario
      setName(getNextScenarioName());
      setTariffEnabled(true);
      setKappaEnabled(false);
      setKappaConfig(EMPTY_KAPPA_CONFIG);
      setTariffRate(20);
      setMode('Increment');
      setSelectedImporters([]);
      setSelectedExporters([]);
      setSelectedSectors([]);
      setCustomRules([]);
      setTariffMode('uniform');
    }
    // Initialize a draft once; new runs must not reset edits in this form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingScenario]);

  if (!isOpen) return null;

  const countries = [...(baselineModel?.country_list || [])].sort((a, b) => a.localeCompare(b));
  const sectors = baselineModel?.sector_list || [];
  const allCountries = baselineModel?.country_list || []; // unsorted, for index consistency
  const hasEoraSectorGroups = selectedDataset === 'calibration_2017_40c';

  const buildConfig = () => {
    const scenarioName = name.trim() || getNextScenarioName();
    const tariffData: TariffConfiguration['tariff_data'] = {};

    if (tariffMode === 'uniform') {
      // Uniform: Cartesian product at single rate
      const imps = selectedImporters ?? countries;
      const exps = selectedExporters ?? countries;
      const secs = selectedSectors ?? sectors;

      imps.forEach(imp => {
        exps.forEach(exp => {
          if (imp !== exp) {
            secs.forEach(sec => {
              tariffData[`${imp}|${exp}|${sec}`] = { rate: tariffRate, mode };
            });
          }
        });
      });

      const tariffConfig: TariffConfiguration = {
        importers: imps,
        exporters: exps,
        sectors: secs,
        tariff_data: tariffData,
        tariffMode: 'uniform',
      };

      return { scenarioName, tariffConfig };
    }

    // Custom mode: apply rules sorted by specificity (broader first)
    // Wildcards (ALL, TRADABLE_SECTORS, NONTRADABLE_SECTORS) count as low specificity.
    const isSectorWildcard = (s: string) =>
      s === ALL || s === TRADABLE_SECTORS || s === NONTRADABLE_SECTORS;
    const specificity = (r: TariffRule) =>
      (r.importer === ALL ? 0 : 1) + (r.exporter === ALL ? 0 : 1) + (isSectorWildcard(r.sector) ? 0 : 1);

    const sorted = [...customRules].sort((a, b) => specificity(a) - specificity(b));

    const affectedImps = new Set<string>();
    const affectedExps = new Set<string>();
    const affectedSecs = new Set<string>();

    sorted.forEach(rule => {
      const rImps = rule.importer === ALL ? allCountries : [rule.importer];
      const rExps = rule.exporter === ALL ? allCountries : [rule.exporter];
      const rSecs = expandSectorWildcard(rule.sector, sectors);

      rImps.forEach(imp => {
        rExps.forEach(exp => {
          if (imp !== exp) {
            rSecs.forEach(sec => {
              tariffData[`${imp}|${exp}|${sec}`] = { rate: rule.rate, mode: rule.mode };
            });
          }
        });
      });

      rImps.forEach(c => affectedImps.add(c));
      rExps.forEach(c => affectedExps.add(c));
      rSecs.forEach(s => affectedSecs.add(s));
    });

    const tariffConfig: TariffConfiguration = {
      importers: Array.from(affectedImps),
      exporters: Array.from(affectedExps),
      sectors: Array.from(affectedSecs),
      tariff_data: tariffData,
      tariffMode: 'custom',
      customRules: customRules,
    };

    return { scenarioName, tariffConfig };
  };

  // buildConfig() always builds a tariffConfig from the current form fields;
  // whether it's actually sent depends on tariffEnabled (resolved at save/solve time).
  const finalTariffConfig = (built: TariffConfiguration): TariffConfiguration =>
    tariffEnabled ? built : EMPTY_TARIFF_CONFIG;
  const finalKappaConfig = (): KappaConfig | undefined =>
    kappaEnabled ? kappaConfig : undefined;

  // Check applicability without expanding hundreds of thousands of EXIOBASE cells
  // on every keystroke. The full API payload is built only when submitting.
  const hasForeignPair = (importers: string[], exporters: string[]) => importers.some(importer => exporters.some(exporter => exporter !== importer));
  const hasConfiguredTariff = tariffMode === 'uniform'
    ? (selectedSectors ?? sectors).length > 0 && hasForeignPair(selectedImporters ?? countries, selectedExporters ?? countries)
    : customRules.some(rule => expandSectorWildcard(rule.sector, sectors).length > 0 && hasForeignPair(rule.importer === ALL ? countries : [rule.importer], rule.exporter === ALL ? countries : [rule.exporter]));
  const portfolioInputsValid = !kappaEnabled || (Number.isFinite(kappaConfig.eta) && (kappaConfig.eta ?? 0) > 0);
  const canSave = targets.length > 0 && !!baselineModel && !isLoading && !modelError && portfolioInputsValid && (
    (tariffEnabled && hasConfiguredTariff) ||
    (kappaEnabled && hasKappaShock(kappaConfig)));

  const handleSolve = async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      const { scenarioName, tariffConfig } = buildConfig();
      const finalTariff = finalTariffConfig(tariffConfig);
      const finalKappa = finalKappaConfig();
      for (const target of targets) {
        await addScenario(target, scenarioName, finalTariff, finalKappa);
      }
      setSubmitted(true);
    } catch (err) {
      setSaveError('Failed to solve scenario. Please check the connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const selectionLabel = (items: string[] | null, all: string, options: string[]) => items === null ? all : !items.length ? '0 selected' : items.length === options.length ? all : items.length <= 2 ? items.join(', ') : `${items.length} selected`;
  return <section className="scenario-editor" aria-label="Scenario configuration">
    <header className="editor-heading"><div><h2>{editingScenario ? 'Revise scenario' : 'New scenario'}</h2></div>
      <button className="simulation-text-button" onClick={onClose}>New draft</button></header>
    {editingScenario?.status === 'solved' && <p className="control-help">Based on {editingScenario.name}. This creates a new run; the original result stays available.</p>}
    <div className="editor-fields">
      <label className="editor-field">Dataset<select aria-label="Scenario dataset" value={selectedDataset} disabled={!!editingScenario || isSaving} onChange={e => {
        const next = e.target.value;
        setSelectedDataset(next);
        const compatible = MODEL_TARGETS.filter(t => t.dataset === next);
        const retained = selectedFrameworks.filter(f => compatible.some(t => t.framework === f));
        setSelectedFrameworks(retained.length ? retained : [compatible[0].framework]);
        setSelectedImporters([]); setSelectedExporters([]); setSelectedSectors([]); setCustomRules([]);
        setKappaEnabled(false); setKappaConfig(EMPTY_KAPPA_CONFIG); setSaveError(null);
      }}>{DATASETS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}</select></label>
      <fieldset className="editor-models"><legend>Run with</legend>{availableTargets.map(target => <label key={target.framework}>
        <input type="checkbox" aria-label={`Run ${target.label}`} checked={targets.some(t => t.framework === target.framework)}
          disabled={!!editingScenario || isSaving || (kappaEnabled && !frameworkInfo[target.framework]?.supports_kappa)} onChange={e => {
            const next = e.target.checked ? [...selectedFrameworks, target.framework] : selectedFrameworks.filter(f => f !== target.framework);
            setSelectedFrameworks(next); if (!next.length) setKappaEnabled(false);
          }} />{target.label}</label>)}</fieldset>
      {isLoading && !modelError && <p role="status">Loading selected models…</p>}
      {modelError && <p role="alert">{modelError} <button onClick={() => setModelRetry(n => n + 1)}>Retry</button></p>}
      <label className="editor-field">Scenario name<input aria-label="Scenario name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. US tariff +10 pp" /></label>
      <label className="shock-toggle"><input type="checkbox" checked={tariffEnabled} onChange={e => setTariffEnabled(e.target.checked)} />Tariff policy</label>
      {tariffEnabled && <>
        <div className="option-toggle" role="group" aria-label="Tariff rules"><button aria-pressed={tariffMode === 'uniform'} onClick={() => setTariffMode('uniform')}>Uniform change</button><button aria-pressed={tariffMode === 'custom'} onClick={() => setTariffMode('custom')}>Custom rules</button></div>
        {tariffMode === 'uniform' ? <>
          <MultiSelect label="Importers" options={countries} selected={selectedImporters} onSelectionChange={setSelectedImporters} />
          <MultiSelect label="Exporters" options={countries} selected={selectedExporters} onSelectionChange={setSelectedExporters} />
          <MultiSelect label="Sectors" options={sectors} selected={selectedSectors} onSelectionChange={setSelectedSectors} wildcards={hasEoraSectorGroups ? [
            {label:'Tradable',onApply:()=>setSelectedSectors(expandSectorWildcard(TRADABLE_SECTORS,sectors))},
            {label:'Non-tradable',onApply:()=>setSelectedSectors(expandSectorWildcard(NONTRADABLE_SECTORS,sectors))},
          ] : []} />
          <div className="tariff-value"><label className="editor-field">Action<select aria-label="Tariff action" value={mode} onChange={e => setMode(e.target.value as typeof mode)}><option value="Increment">Increase by</option><option value="Set Level">Set tariff to</option></select></label>
            <label className="editor-field">{mode === 'Increment' ? 'Percentage points' : 'Percent'}<input aria-label="Tariff rate" type="number" value={tariffRate} onChange={e => setTariffRate(Number(e.target.value))} /></label></div>
        </> : <div className="custom-policy-rules"><p className="control-help">Specific rules replace broader rules.</p><TariffOverrideTable rules={customRules} onChange={setCustomRules} countries={countries} sectors={sectors} hasEoraSectorGroups={hasEoraSectorGroups} /></div>}
      </>}
      {currentFrameworkSupportsKappa && <details className="advanced-policy" open={kappaEnabled || undefined}><summary>Capital-friction policy</summary>
        <label className="shock-toggle"><input type="checkbox" checked={kappaEnabled} onChange={e => setKappaEnabled(e.target.checked)} />Include capital-friction shock</label>
        {kappaEnabled && baselineModel && <div className="capital-policy-editor"><KappaShockEditor countries={allCountries} lam={(baselineModel.params.lam as number[][]) ?? []} config={kappaConfig} onChange={setKappaConfig} /></div>}
      </details>}
      {targets.length > 1 && <p className="control-help">Capital-friction policy is available when only Furusawa2023 is selected.</p>}
    </div>
    <footer className="editor-submit">
      <div className="policy-summary" aria-label="Policy summary">
        {tariffEnabled && (tariffMode === 'uniform' ? <p><strong>{selectionLabel(selectedImporters,'All importers',countries)}</strong> · {selectionLabel(selectedExporters,'all foreign suppliers',countries)} · {selectionLabel(selectedSectors,'all sectors',sectors)}<br />{mode === 'Increment' ? `Tariffs +${tariffRate} percentage points` : `Tariffs set to ${tariffRate}%`}</p> : <p>{customRules.length} custom tariff rules</p>)}
        {kappaEnabled && <p>Capital-friction shock included</p>}
        <small>{targets.map(t=>t.framework).join(' + ') || 'Select a model'}</small>
      </div>
      {saveError && <p role="alert">{saveError}</p>}
      {submitted && <p role="status" className="control-help">Submitted. Follow progress in Runs; results appear when complete.</p>}
      <button onClick={handleSolve} disabled={isLoading || isSaving || !canSave} className="simulation-button simulation-button-primary">
        {isSaving ? 'Submitting…' : targets.length > 1 ? 'Run both models' : 'Run scenario'}
      </button>
      {solvingCount > 0 && <p role="status" className="control-help">{solvingCount} running or queued</p>}
    </footer>
  </section>;
};

export default ScenarioEditor;
